import { CloseButton } from '@components/utility/buttons';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Dialog, DialogProps, Typography } from '@mui/material';
import { toLogin } from '@utils/route';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

enum FailedStatus {
    MAX_CAPACITY = 'max_capacity',
    LOGIN = 'invalid_email_authenticate',
    MISC = 'invalid_email_event',
}

interface Props extends DialogProps {
    status?: FailedStatus;
    enableWaitingList?: boolean;
}

const RegistrationFailedDialog = ({ status, enableWaitingList, ...props }: Props) => {
    return (
        <Dialog
            {...props}
            disableEscapeKeyDown
            slotProps={{
                paper: {
                    className:
                        'flex rounded-lg items-center mx-4 min-w-0 py-16 px-2 sm:max-w-[630px] sm:px-12 md:overflow-hidden text-center',
                },
            }}
        >
            <CloseButton className="size-10 flex" color="black" onClick={() => props.onClose?.({}, 'escapeKeyDown')} />
            <div className="border-neutral-100 rounded-full p-5 border-2">
                <CloseIcon fontSize="large" />
            </div>
            <Typography fontFamily="museo-sans" fontSize={57} fontWeight={600} letterSpacing={-1.44}>
                {enableWaitingList ? (
                    <FormattedMessage
                        id="event.error.joinWaitingListFailed"
                        defaultMessage="Join waiting list failed"
                    />
                ) : (
                    <FormattedMessage id="event.error.registrationFailed" defaultMessage="Registration Failed" />
                )}
            </Typography>
            <Typography fontSize={24}>
                {status === FailedStatus.MAX_CAPACITY ? (
                    <FormattedMessage
                        id="event.error.maxCapacity"
                        defaultMessage="Unable to register. This event has reached maximum capacity."
                    />
                ) : status === FailedStatus.LOGIN ? (
                    enableWaitingList ? (
                        <FormattedMessage
                            id="event.error.loginWaitingList"
                            defaultMessage="We couldn't add you to the waiting list. This email may already be on the waiting list or may require you to login. Please login with your Victron Professional account if you have one, or contact support for help."
                        />
                    ) : (
                        <FormattedMessage
                            id="event.error.loginRegistration"
                            defaultMessage="We couldn't complete your registration. This email may already be registered or may require you to login. Please login with your Victron Professional account if you have one, or contact support for help."
                        />
                    )
                ) : enableWaitingList ? (
                    <FormattedMessage
                        id="event.error.loginWaitingList.description"
                        defaultMessage="We couldn't add you to the waiting list. This email may already be registered or may require you to login or there may be another issue. If you believe this is a mistake, please check your inbox or contact support for help."
                    />
                ) : (
                    <FormattedMessage
                        id="event.error.loginRegistration.description"
                        defaultMessage="We couldn't complete your registration. This email may already be registered or may require you to login or there may be another issue. If you believe this is a mistake, please check your inbox or contact support for help."
                    />
                )}
            </Typography>
            {status === FailedStatus.LOGIN && (
                <Button className="rounded-full w-full mt-10" component={Link} to={toLogin()}>
                    <Typography variant="body2" fontFamily={'inter'} fontWeight={500}>
                        <FormattedMessage id="common.login" defaultMessage="Login" />
                    </Typography>
                </Button>
            )}
        </Dialog>
    );
};

export { FailedStatus, RegistrationFailedDialog };
